/**
 * The feed (prototype MyFeed) against api-next's `feed` module at /v2: posts from
 * the diner and the people they follow, reactions, comments, Report and Block.
 * Signed-in only; calls go through v2Session (lists) for the token renewal.
 */

export type FeedPostType = 'TEXT' | 'LIST_SAVE' | 'RESTAURANT';

export interface FeedAuthor {
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
}

export interface FeedReaction {
    emoji: string;
    count: number;
    reacted: boolean;
}

export interface FeedItem {
    legacyRestaurantId: string | null;
    legacyMealId: string | null;
    name: string | null;
    path: string | null;
    restaurantName: string | null;
    price: { display: string } | null;
    image: { templatedUrl: string } | null;
}

/** A restaurant, from the search index (null fields when it has gone). */
export interface FeedRestaurant {
    legacyRestaurantId: string;
    name: string | null;
    locality: string | null;
    path: string | null;
    image: { templatedUrl: string } | null;
}

export interface FeedImage {
    url: string;
    /** Resizable: {size} becomes e.g. 640x480-fit=cover. */
    templatedUrl: string;
}

export interface FeedPost {
    id: string;
    type: FeedPostType;
    /** Null on a restaurant's post (`restaurant` says who). */
    author: FeedAuthor | null;
    restaurant: FeedRestaurant | null;
    images: FeedImage[];
    content: string;
    createdAt: string;
    mine: boolean;
    reactions: FeedReaction[];
    commentCount: number;
    item: FeedItem | null;
    list: { name: string; slug: string } | null;
}

export interface RestaurantStory {
    id: string;
    restaurant: FeedRestaurant;
    image: FeedImage;
    caption: string | null;
    item: FeedItem | null;
    createdAt: string;
    expiresAt: string;
}

export interface RestaurantStoryGroup {
    restaurant: FeedRestaurant;
    stories: RestaurantStory[];
}

export interface FeedComment {
    id: string;
    author: FeedAuthor;
    content: string;
    createdAt: string;
    mine: boolean;
}

export type ReportReason = 'SPAM' | 'ABUSE' | 'INAPPROPRIATE' | 'OTHER';

/** api-next's limits (resolvers/feed FEED_LIMITS). */
export const MAX_POST = 1000;
export const MAX_COMMENT = 500;
export const MAX_REPORT_DETAILS = 1000;

/** The prototype's reaction set, spelled exactly as api-next accepts them. */
export const REACTIONS = [
    { emoji: '👍', label: 'Like' },
    { emoji: '❤️', label: 'Love' },
    { emoji: '😋', label: 'Yum' },
    { emoji: '🔥', label: 'Fire' },
] as const;

export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
    { value: 'SPAM', label: 'Spam' },
    { value: 'ABUSE', label: 'Harassment or abuse' },
    { value: 'INAPPROPRIATE', label: 'Inappropriate content' },
    { value: 'OTHER', label: 'Something else' },
];

const AUTHOR = 'author { username displayName avatarUrl }';
const RESTAURANT =
    'legacyRestaurantId name locality path image { templatedUrl }';
const POST = `id type content createdAt mine commentCount ${AUTHOR}
    restaurant { ${RESTAURANT} } images { url templatedUrl }
    reactions { emoji count reacted }
    item { legacyRestaurantId legacyMealId name path restaurantName price { display } image { templatedUrl } }
    list { name slug }`;
const COMMENT = `id content createdAt mine ${AUTHOR}`;

export const VIEWER = `query FeedViewer { viewer { username displayName avatarUrl } }`;
export const MY_FEED = `query MyFeed($before: String) {
    myFeed(before: $before) { posts { ${POST} } nextCursor }
}`;
const STORY = `id caption createdAt expiresAt image { url templatedUrl } restaurant { ${RESTAURANT} }
    item { legacyRestaurantId legacyMealId name path restaurantName price { display } image { templatedUrl } }`;
export const RESTAURANT_FEED = `query RestaurantFeed($before: String) {
    restaurantFeed(before: $before) { posts { ${POST} } nextCursor }
}`;
export const FOLLOWED_STORIES = `query FollowedRestaurantStories {
    followedRestaurantStories { restaurant { ${RESTAURANT} } stories { ${STORY} } }
}`;
export const MY_FOLLOWED_RESTAURANTS = `query MyFollowedRestaurants { myFollowedRestaurants(limit: 5) { ${RESTAURANT} } }`;
export const RESTAURANT_UPDATES = `query RestaurantUpdates($id: ID!, $before: String) {
    restaurantUpdates(legacyRestaurantId: $id, before: $before) {
        stories { ${STORY} }
        posts { posts { ${POST} } nextCursor }
    }
}`;
export const FOLLOW_RESTAURANT = `mutation FollowRestaurant($id: ID!) { followRestaurant(legacyRestaurantId: $id) }`;
export const UNFOLLOW_RESTAURANT = `mutation UnfollowRestaurant($id: ID!) { unfollowRestaurant(legacyRestaurantId: $id) }`;
export const POST_COMMENTS = `query PostComments($postId: ID!) {
    postComments(postId: $postId) { ${COMMENT} }
}`;
export const CREATE_POST = `mutation CreatePost($content: String!) { createPost(content: $content) { ${POST} } }`;
export const DELETE_POST = `mutation DeletePost($id: ID!) { deletePost(id: $id) }`;
export const ADD_COMMENT = `mutation AddComment($postId: ID!, $content: String!) {
    addComment(postId: $postId, content: $content) { ${COMMENT} }
}`;
export const DELETE_COMMENT = `mutation DeleteComment($id: ID!) { deleteComment(id: $id) }`;
export const ADD_REACTION = `mutation AddReaction($postId: ID!, $emoji: String!) { addReaction(postId: $postId, emoji: $emoji) }`;
export const REMOVE_REACTION = `mutation RemoveReaction($postId: ID!, $emoji: String!) {
    removeReaction(postId: $postId, emoji: $emoji)
}`;
export const REPORT_POST = `mutation ReportPost($postId: ID!, $reason: ReportReason!, $details: String) {
    reportPost(postId: $postId, reason: $reason, details: $details)
}`;
export const REPORT_COMMENT = `mutation ReportComment($commentId: ID!, $reason: ReportReason!, $details: String) {
    reportComment(commentId: $commentId, reason: $reason, details: $details)
}`;
export interface FollowSuggestion {
    profile: FeedAuthor;
    publicListCount: number;
}

export const WHO_TO_FOLLOW = `query WhoToFollow($limit: Int) {
    whoToFollow(limit: $limit) { publicListCount profile { username displayName avatarUrl } }
}`;
export const BLOCK_USER = `mutation BlockUser($username: String!) { blockUser(username: $username) }`;

export const authorName = (a: FeedAuthor) => a.displayName || a.username;

/** Who posted: the diner's name, or the restaurant's. */
export const posterName = (post: Pick<FeedPost, 'author' | 'restaurant'>) =>
    post.author
        ? authorName(post.author)
        : (post.restaurant?.name ?? 'A restaurant');

/** templatedUrl at a size (the images host's {size} slot). */
export const sized = (image: { templatedUrl: string }, size: string) =>
    image.templatedUrl.replace('{size}', size);

/** Up to two initials for an avatar without a photo ("Priya Shah" -> "PS"). */
export function initials(name: string): string {
    const words = name
        .trim()
        .split(/[\s_.-]+/)
        .filter(Boolean);
    const letters =
        words.length > 1
            ? [words[0], words[words.length - 1]].map(w => [...w][0])
            : [...(words[0] ?? '')].slice(0, 2);
    return letters.join('').toUpperCase() || '?';
}

/** The prototype's short ages: "now", "45m", "2h", "3d", then the date ("12 Oct"). */
export function timeAgo(iso: string, now: Date = new Date()): string {
    const then = new Date(iso);
    const seconds = Math.max(0, (now.getTime() - then.getTime()) / 1000);
    if (Number.isNaN(seconds)) return '';
    if (seconds < 60) return 'now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`;
    return then.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        ...(then.getFullYear() === now.getFullYear()
            ? {}
            : { year: 'numeric' }),
    });
}

export const listCountText = (n: number) => (n === 1 ? '1 list' : `${n} lists`);

export const commentCountText = (n: number) =>
    n === 1 ? '1 comment' : `${n} comments`;

/**
 * Reactions after the viewer toggles `emoji`, for an optimistic update: in the
 * prototype's order, reacted state flipped, emptied reactions dropped.
 */
export function toggleReaction(
    reactions: FeedReaction[],
    emoji: string,
): FeedReaction[] {
    const current = reactions.find(r => r.emoji === emoji);
    const next: FeedReaction = current
        ? {
              emoji,
              reacted: !current.reacted,
              count: current.count + (current.reacted ? -1 : 1),
          }
        : { emoji, reacted: true, count: 1 };
    const others = reactions.filter(r => r.emoji !== emoji);
    const order = (e: string) => {
        const i = REACTIONS.findIndex(r => r.emoji === e);
        return i < 0 ? REACTIONS.length : i;
    };
    return [...others, ...(next.count > 0 ? [next] : [])].sort(
        (a, b) => order(a.emoji) - order(b.emoji),
    );
}
